import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart3, TrendingUp, Percent, Wallet, Coins, Users, Loader2, RefreshCw, ChevronDown, ListFilter, Sparkles,
} from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_LABELS = {
  abierto: "Abierto", cotizaciones: "Cotizaciones", cerrado: "Cerrado",
  terminado: "Terminado", archivado: "Archivado", cancelado: "Cancelado", viaje_empresa: "Viaje de Empresa",
};
// 'viaje_empresa' EXCLUIDO por defecto de Margen/Rentabilidad (no son ingresos de cliente).
// Sigue seleccionable en el popover de estados si se quiere ver puntualmente.
const SOLD_DEFAULT = ["abierto", "cerrado", "terminado"];
const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const mesLabel = (m) => { const [y, mm] = (m || "").split("-"); return mm ? `${MESES[parseInt(mm, 10) - 1]} ${y}` : m; };

const eur = (n) => (n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const eur2 = (n) => (n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });
const pct = (n) => `${(n || 0).toFixed(1)}%`;

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addMonths = (d, n) => { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; };

const PERIODOS = [
  { key: "este_ano", label: "Este año" },
  { key: "mes_pasado", label: "Mes pasado" },
  { key: "3m", label: "Últimos 3 meses" },
  { key: "6m", label: "Últimos 6 meses" },
  { key: "1a", label: "Último año" },
  { key: "custom", label: "Personalizado" },
];

function presetRange(p) {
  const today = new Date();
  const y = today.getFullYear();
  if (p === "este_ano") return { desde: `${y}-01-01`, hasta: iso(today) };
  if (p === "mes_pasado") {
    const first = new Date(y, today.getMonth() - 1, 1);
    const last = new Date(y, today.getMonth(), 0);
    return { desde: iso(first), hasta: iso(last) };
  }
  if (p === "3m") return { desde: iso(addMonths(today, -3)), hasta: iso(today) };
  if (p === "6m") return { desde: iso(addMonths(today, -6)), hasta: iso(today) };
  if (p === "1a") return { desde: iso(addMonths(today, -12)), hasta: iso(today) };
  return null;
}

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="flex items-center gap-3 p-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Icon className="h-5 w-5" /></div>
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="truncate font-heading text-xl font-bold tracking-tight">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function EstadosPopover({ estados, setEstados, idPrefix }) {
  const toggle = (k) => setEstados((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 gap-2" data-testid={`${idPrefix}-estados-btn`}>
          <ListFilter className="h-4 w-4" /> Estados · {estados.length}
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-2">
        <p className="px-2 py-1 text-xs font-medium text-muted-foreground">Estados del viaje</p>
        {Object.entries(STATUS_LABELS).map(([k, label]) => (
          <label key={k} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted" data-testid={`${idPrefix}-status-${k}`}>
            <Checkbox checked={estados.includes(k)} onCheckedChange={() => toggle(k)} />
            {label}
          </label>
        ))}
        <div className="mt-1 flex justify-between border-t px-2 pt-2">
          <button className="text-xs text-primary hover:underline" onClick={() => setEstados(Object.keys(STATUS_LABELS))}>Todos</button>
          <button className="text-xs text-muted-foreground hover:underline" onClick={() => setEstados(SOLD_DEFAULT)}>Solo vendidos</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Informe mensual transpuesto reutilizable: meses en columnas, indicadores en filas.
 * Clic en un indicador → gráfica de barras mes a mes.
 */
function MonthlyReport({ rows, cards, defaultKpi, idPrefix, defaultEstados = SOLD_DEFAULT, note, fechaPor = "venta" }) {
  const [periodo, setPeriodo] = useState("este_ano");
  const [custom, setCustom] = useState(() => presetRange("3m"));
  const [estados, setEstados] = useState(defaultEstados);
  const [agente, setAgente] = useState("all");
  const [selKpi, setSelKpi] = useState(defaultKpi || rows[0].key);

  const range = periodo === "custom" ? custom : presetRange(periodo);
  const desde = range.desde, hasta = range.hasta;

  const q = useQuery({
    queryKey: ["bi-ventas-agente", desde, hasta, estados, agente, fechaPor],
    queryFn: async () => (await api.get("/bi/ventas-agente", { params: { desde, hasta, estados: estados.join(","), agente, fecha_por: fechaPor } })).data,
    enabled: estados.length > 0 && !!desde && !!hasta,
  });
  const data = q.data;
  const total = data?.total || {};
  const meses = data?.meses || [];
  const agentes = data?.agentes_disponibles || [];
  const selRow = rows.find((r) => r.key === selKpi) || rows[0];
  const chartData = meses.map((m) => ({ name: mesLabel(m.mes), v: selRow.get(m) || 0 }));

  return (
    <div className="space-y-5">
      {/* Filtros compactos en una sola fila */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={agente} onValueChange={setAgente}>
          <SelectTrigger className="h-9 w-[210px]" data-testid={`${idPrefix}-agente`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los agentes (agregado)</SelectItem>
            {agentes.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={periodo} onValueChange={setPeriodo}>
          <SelectTrigger className="h-9 w-[170px]" data-testid={`${idPrefix}-periodo`}><SelectValue /></SelectTrigger>
          <SelectContent>{PERIODOS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
        </Select>

        {periodo === "custom" && (
          <div className="flex items-center gap-1.5 rounded-md border px-2 py-1">
            <Input type="date" value={custom.desde} max={custom.hasta} onChange={(e) => setCustom((c) => ({ ...c, desde: e.target.value }))} className="h-7 w-[140px] border-0 p-0 shadow-none focus-visible:ring-0" data-testid={`${idPrefix}-desde`} />
            <span className="text-xs text-muted-foreground">–</span>
            <Input type="date" value={custom.hasta} min={custom.desde} onChange={(e) => setCustom((c) => ({ ...c, hasta: e.target.value }))} className="h-7 w-[140px] border-0 p-0 shadow-none focus-visible:ring-0" data-testid={`${idPrefix}-hasta`} />
          </div>
        )}

        <EstadosPopover estados={estados} setEstados={setEstados} idPrefix={idPrefix} />

        <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground" data-testid={`${idPrefix}-fecha-basis`}>
          por fecha de {fechaPor === "viaje" ? "viaje" : "venta"}
        </span>

        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => q.refetch()} disabled={q.isFetching} data-testid={`${idPrefix}-refresh`}>
          {q.isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      {note && (
        <p className="rounded-md bg-muted/60 px-3 py-2 text-xs text-muted-foreground" data-testid={`${idPrefix}-note`}>{note}</p>
      )}

      {!q.isLoading && (total.ventas || 0) === 0 && (
        <p className="rounded-md border border-dashed px-4 py-6 text-center text-sm text-muted-foreground" data-testid={`${idPrefix}-empty`}>
          No hay viajes en este rango/estados. {["bi-rent", "bi-eff"].includes(idPrefix) ? "La rentabilidad y la eficiencia se calculan cuando el viaje pasa a Cerrado o Terminado (con el coste real cargado)." : ""}
        </p>
      )}

      {q.isLoading ? (
        <Skeleton className="h-72 w-full" />
      ) : (total.ventas || 0) === 0 ? null : (
        <>
          {/* KPIs del periodo */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {cards.map((c, i) => <Kpi key={i} icon={c.icon} label={c.label} value={c.fn(total)} testid={`${idPrefix}-kpi-${i}`} />)}
          </div>

          {/* Tabla: meses en horizontal, indicadores en vertical. Clic en una fila → gráfica */}
          <Card>
            <CardContent className="p-0">
              <p className="px-4 pt-3 text-xs text-muted-foreground">Haz clic en un indicador para ver su gráfica mes a mes.</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid={`${idPrefix}-table`}>
                  <thead>
                    <tr className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="sticky left-0 z-10 bg-muted/40 px-4 py-3 font-semibold">Indicador</th>
                      {meses.map((m) => <th key={m.mes} className="px-4 py-3 text-right font-semibold capitalize">{mesLabel(m.mes)}</th>)}
                      <th className="px-4 py-3 text-right font-semibold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {meses.length === 0 ? (
                      <tr><td className="px-4 py-10 text-center text-muted-foreground">Sin datos en este periodo.</td></tr>
                    ) : rows.map((row) => {
                      const active = selKpi === row.key;
                      return (
                        <tr key={row.key} onClick={() => setSelKpi(row.key)} data-testid={`${idPrefix}-kpirow-${row.key}`}
                          className={`cursor-pointer border-b transition-colors ${active ? "bg-primary/10" : "hover:bg-muted/40"}`}>
                          <td className={`sticky left-0 z-10 px-4 py-3 font-medium ${active ? "bg-primary/10" : "bg-background"}`}>
                            <span className="flex items-center gap-2">
                              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: row.color }} />
                              {row.label}
                            </span>
                          </td>
                          {meses.map((m) => <td key={m.mes} className="px-4 py-3 text-right tabular-nums">{row.fmt(row.get(m))}</td>)}
                          <td className="px-4 py-3 text-right font-semibold tabular-nums">{row.fmt(row.get(total))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Gráfica del indicador seleccionado */}
          {meses.length > 0 && (
            <Card data-testid={`${idPrefix}-chart`}>
              <CardContent className="p-4">
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
                  <BarChart3 className="h-4 w-4" style={{ color: selRow.color }} />
                  {selRow.label} · evolución mes a mes
                </h3>
                <div className="h-72 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} width={64} />
                      <Tooltip formatter={(v) => [selRow.fmt(v), selRow.label]} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                      <Bar dataKey="v" fill={selRow.color} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ---- Ventas por agente (MARGEN DE VENTA: foto al vender = Ingresado − Cotizado Total) ----
const VENTAS_ROWS = [
  { key: "ventas", label: "Ventas (nº viajes)", color: "#6366f1", fmt: (v) => v ?? 0, get: (m) => m.ventas },
  { key: "leads", label: "Leads cogidos", color: "#0ea5e9", fmt: (v) => v ?? 0, get: (m) => m.leads },
  { key: "conversion_pct", label: "Conversión", color: "#14b8a6", fmt: pct, get: (m) => m.conversion_pct },
  { key: "volumen_eur", label: "Volumen de ventas (precio final)", color: "#22c55e", fmt: eur, get: (m) => m.volumen_eur },
  { key: "margen_venta_eur", label: "Margen de venta (Ingresado − Cotizado)", color: "#f59e0b", fmt: eur, get: (m) => m.margen_venta_eur },
  { key: "margen_venta_medio_eur", label: "Margen de venta medio", color: "#a855f7", fmt: eur2, get: (m) => m.margen_venta_medio_eur },
  { key: "margen_venta_pct", label: "Margen de venta % (÷ precio final)", color: "#ec4899", fmt: pct, get: (m) => m.margen_venta_pct },
];
const VENTAS_CARDS = [
  { icon: TrendingUp, label: "Ventas (nº viajes)", fn: (t) => t.ventas ?? 0 },
  { icon: Wallet, label: "Volumen (precio final)", fn: (t) => eur(t.volumen_eur) },
  { icon: Coins, label: "Margen de venta", fn: (t) => eur(t.margen_venta_eur) },
  { icon: Percent, label: "Margen de venta %", fn: (t) => pct(t.margen_venta_pct) },
];

// ---- Rentabilidad (POST-VIAJE: Ingresado − Coste real; % = margen ÷ precio final) ----
// KPIs por pasajero (clave para Dirección): rentabilidad ÷ pax, margen de venta ÷ pax, media pax/viaje.
const rentabilidadPax = (m) => (m.pax ? (m.margen_real_eur || 0) / m.pax : 0);
const margenVentaPax = (m) => (m.pax ? (m.margen_venta_eur || 0) / m.pax : 0);
const paxPorViaje = (m) => (m.ventas ? (m.pax || 0) / m.ventas : 0);
const num1 = (v) => (v || 0).toLocaleString("es-ES", { maximumFractionDigits: 1 });
const RENT_ROWS = [
  { key: "ventas", label: "Nº de viajes", color: "#6366f1", fmt: (v) => v ?? 0, get: (m) => m.ventas },
  { key: "pax", label: "Nº de pasajeros", color: "#0ea5e9", fmt: (v) => v ?? 0, get: (m) => m.pax },
  { key: "volumen_eur", label: "Volumen (precio final)", color: "#22c55e", fmt: eur, get: (m) => m.volumen_eur },
  { key: "coste_real_eur", label: "Coste real", color: "#ef4444", fmt: eur, get: (m) => m.coste_real_eur },
  { key: "margen_real_eur", label: "Rentabilidad (Ingresado − Coste real)", color: "#f59e0b", fmt: eur, get: (m) => m.margen_real_eur },
  { key: "margen_real_medio_eur", label: "Rentabilidad media / viaje", color: "#a855f7", fmt: eur2, get: (m) => m.margen_real_medio_eur },
  { key: "rentabilidad_pct", label: "Rentabilidad % (margen ÷ precio final)", color: "#ec4899", fmt: pct, get: (m) => m.rentabilidad_pct },
  { key: "rentabilidad_pax", label: "Rentabilidad / pasajero", color: "#10b981", fmt: eur2, get: rentabilidadPax },
  { key: "margen_venta_pax", label: "Margen de venta / pasajero", color: "#f97316", fmt: eur2, get: margenVentaPax },
  { key: "pax_por_viaje", label: "Media de pax / viaje", color: "#8b5cf6", fmt: num1, get: paxPorViaje },
];
const RENT_CARDS = [
  { icon: Coins, label: "Rentabilidad total", fn: (t) => eur(t.margen_real_eur) },
  { icon: Percent, label: "Rentabilidad %", fn: (t) => pct(t.rentabilidad_pct) },
  { icon: Users, label: "Rentabilidad / pasajero", fn: (t) => eur2(rentabilidadPax(t)) },
  { icon: Wallet, label: "Margen de venta / pasajero", fn: (t) => eur2(margenVentaPax(t)) },
  { icon: Users, label: "Media de pax / viaje", fn: (t) => num1(paxPorViaje(t)) },
  { icon: TrendingUp, label: "Nº de viajes", fn: (t) => t.ventas ?? 0 },
];

// ---- Por pasajero ----  (precio medio por pax = volumen ÷ pax; rentabilidad media por pax = rentabilidad ÷ pax)
const precioMedioPax = (m) => (m.pax ? m.volumen_eur / m.pax : 0);
const rentMediaPax = (m) => (m.pax ? m.margen_real_eur / m.pax : 0);
const PAX_ROWS = [
  { key: "pax", label: "Nº de pasajeros", color: "#6366f1", fmt: (v) => v ?? 0, get: (m) => m.pax },
  { key: "ventas", label: "Nº de viajes", color: "#0ea5e9", fmt: (v) => v ?? 0, get: (m) => m.ventas },
  { key: "volumen_eur", label: "Volumen de ventas", color: "#22c55e", fmt: eur, get: (m) => m.volumen_eur },
  { key: "precio_medio_pax", label: "Precio medio / pax", color: "#a855f7", fmt: eur2, get: precioMedioPax },
  { key: "rent_media_pax", label: "Rentabilidad media / pax", color: "#ec4899", fmt: eur2, get: rentMediaPax },
];
const PAX_CARDS = [
  { icon: Users, label: "Nº de pasajeros", fn: (t) => t.pax ?? 0 },
  { icon: Wallet, label: "Precio medio / pax", fn: (t) => eur2(precioMedioPax(t)) },
  { icon: Coins, label: "Rentabilidad media / pax", fn: (t) => eur2(rentMediaPax(t)) },
  { icon: TrendingUp, label: "Volumen de ventas", fn: (t) => eur(t.volumen_eur) },
];

// ---- Eficiencia de Operaciones: mejora entre Margen de venta y Rentabilidad = Cotizado − Coste real ----
const mejoraEur = (m) => (m.margen_real_eur || 0) - (m.margen_venta_eur || 0);         // = Σ(Cotizado − Coste real)
const mejoraPctCot = (m) => (m.cotizado_eur ? (mejoraEur(m) / m.cotizado_eur) * 100 : 0);
const mejoraPts = (m) => (m.rentabilidad_pct || 0) - (m.margen_venta_pct || 0);         // puntos porcentuales
const pts = (v) => `${(v || 0).toFixed(1)} pts`;
const EFF_ROWS = [
  { key: "ventas", label: "Nº de viajes", color: "#6366f1", fmt: (v) => v ?? 0, get: (m) => m.ventas },
  { key: "cotizado_eur", label: "Cotizado Total (venta)", color: "#0ea5e9", fmt: eur, get: (m) => m.cotizado_eur },
  { key: "coste_real_eur", label: "Coste real (ejecución)", color: "#ef4444", fmt: eur, get: (m) => m.coste_real_eur },
  { key: "mejora_eur", label: "Mejora € (Cotizado − Coste real)", color: "#22c55e", fmt: eur, get: mejoraEur },
  { key: "mejora_pct_cot", label: "Mejora % (sobre cotizado)", color: "#a855f7", fmt: pct, get: mejoraPctCot },
  { key: "mejora_pts", label: "Mejora en puntos (Rentab. − Margen venta)", color: "#ec4899", fmt: pts, get: mejoraPts },
];
const EFF_CARDS = [
  { icon: Coins, label: "Mejora total", fn: (t) => eur(mejoraEur(t)) },
  { icon: Percent, label: "Mejora % s/ cotizado", fn: (t) => pct(mejoraPctCot(t)) },
  { icon: TrendingUp, label: "Mejora en puntos", fn: (t) => pts(mejoraPts(t)) },
  { icon: Wallet, label: "Nº de viajes", fn: (t) => t.ventas ?? 0 },
];

// ============ KimKim: viajes vendidos por el partner ============
const KK_MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

function KpiKk({ label, value, hint, delta, deltaPct, tone = "default", testid }) {
  const positive = (delta ?? 0) >= 0;
  const toneClass = tone === "danger" ? "text-red-600" : tone === "success" ? "text-emerald-600" : "text-primary";
  return (
    <Card data-testid={testid}>
      <CardContent className="p-4">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className={`mt-1 text-xl font-bold tabular-nums ${toneClass}`}>{value}</div>
        {hint ? <div className="mt-0.5 text-[10px] text-muted-foreground">{hint}</div> : null}
        {delta !== undefined && (
          <div className={`mt-1.5 text-[11px] font-medium tabular-nums ${positive ? "text-emerald-600" : "text-red-600"}`}>
            {positive ? "▲" : "▼"} {typeof delta === "number" ? eur(delta) : delta}
            {deltaPct !== undefined ? <span className="ml-1 opacity-80">({positive ? "+" : ""}{deltaPct.toFixed(1)}%)</span> : null}
            <span className="ml-1 text-muted-foreground">vs año anterior</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function KimkimSummaryTab() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [agent, setAgent] = useState("all");

  const q = useQuery({
    queryKey: ["bi-kimkim-summary", year, agent],
    queryFn: async () => (await api.get("/bi/kimkim-summary", { params: { year, agent } })).data,
  });

  if (q.isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (q.isError) {
    return <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">Error cargando datos KimKim.</div>;
  }

  const d = q.data;
  const c = d.current;
  const dl = d.deltas;
  const YEARS = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  const chartData = (c.meses || []).map((m) => ({
    name: (() => { const mm = parseInt(m.mes.split("-")[1], 10); return KK_MESES[mm - 1]; })(),
    "Total price":       Math.round(m.total_price_eur || 0),
    "Ganado por KimKim": Math.round(m.ganado_kk_eur || 0),
    "Ingresado":         Math.round(m.ingresado_eur || 0),
  }));

  const sourceLbl = (s) => s === "scraped" ? "Exacto (HTML)" : s === "sofi1_migration" ? "SOFI 1.0 (migrado)" : s;

  return (
    <div className="space-y-4" data-testid="bi-kimkim">
      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5">
          <Label className="text-xs">Año</Label>
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
            <SelectTrigger className="h-9 w-[120px]" data-testid="bi-kk-year"><SelectValue /></SelectTrigger>
            <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-1.5">
          <Label className="text-xs">Agente</Label>
          <Select value={agent} onValueChange={setAgent}>
            <SelectTrigger className="h-9 w-[210px]" data-testid="bi-kk-agent"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los agentes</SelectItem>
              {(d.agentes_disponibles || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex items-center gap-1.5 rounded-md bg-primary/5 px-2.5 py-1.5 text-[11px] text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          <span>
            <b className="text-foreground">{c.by_source?.scraped ?? 0}</b> exactos ·{" "}
            <b className="text-foreground">{c.by_source?.sofi1_migration ?? 0}</b> migrados de SOFI 1.0
            {c.by_source?.sin_datos ? <> · <b className="text-red-600">{c.by_source.sin_datos}</b> sin datos</> : null}
          </span>
        </div>
      </div>

      {/* KPIs principales */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiKk label="Trips vendidos KimKim" value={c.trips_count.toLocaleString("es-ES")}
               delta={dl.trips_count} testid="bi-kk-kpi-trips" />
        <KpiKk label="Total price (facturado a clientes)" value={eur(c.total_price_eur)}
               delta={dl.total_price_eur} deltaPct={dl.total_price_pct}
               hint="Todo lo que el cliente paga a KimKim" testid="bi-kk-kpi-total" />
        <KpiKk label="Ingresado neto por Espíritu" value={eur(c.ingresado_eur)} tone="success"
               delta={dl.ingresado_eur} deltaPct={dl.ingresado_pct}
               hint="Due to Specialists Total (KimKim → nosotros)" testid="bi-kk-kpi-ingresado" />
        <KpiKk label="Ganado por KimKim con nosotros" value={eur(c.ganado_kk_eur)} tone="danger"
               delta={dl.ganado_kk_eur} deltaPct={dl.ganado_kk_pct}
               hint="Comisión + service fee + carbon (los scraped)" testid="bi-kk-kpi-kk-ganado" />
      </div>

      {/* Pagado directo + cuadre visual */}
      <Card className="border-dashed" data-testid="bi-kk-cuadre">
        <CardContent className="p-4">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Cuadre del reparto del Total price</div>
            <div className="text-[10px] text-muted-foreground">
              {(() => {
                const suma = (c.ingresado_eur || 0) + (c.ganado_kk_eur || 0) + (c.pagado_directo_kk_eur || 0);
                const diff = (c.total_price_eur || 0) - suma;
                const pct = c.total_price_eur ? Math.abs(diff / c.total_price_eur * 100) : 0;
                return `Δ ${eur(diff)} · ${pct.toFixed(2)}% (redondeos)`;
              })()}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-md bg-emerald-50 p-3">
              <div className="text-[10px] uppercase tracking-wide text-emerald-800">= Ingresado Espíritu</div>
              <div className="mt-1 text-lg font-bold tabular-nums text-emerald-700">{eur(c.ingresado_eur)}</div>
            </div>
            <div className="rounded-md bg-rose-50 p-3">
              <div className="text-[10px] uppercase tracking-wide text-rose-800">+ Ganado por KimKim</div>
              <div className="mt-1 text-lg font-bold tabular-nums text-rose-700">{eur(c.ganado_kk_eur)}</div>
            </div>
            <div className="rounded-md bg-sky-50 p-3">
              <div className="text-[10px] uppercase tracking-wide text-sky-800">+ Pagado directo KK → hoteles</div>
              <div className="mt-1 text-lg font-bold tabular-nums text-sky-700" data-testid="bi-kk-kpi-pagado-directo">{eur(c.pagado_directo_kk_eur)}</div>
              <div className="mt-0.5 text-[10px] text-muted-foreground">Tránsito · no queda en KK ni en Espíritu</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPIs secundarios: rentabilidad + markup medio */}
      <div className="grid gap-3 sm:grid-cols-3">
        <KpiKk label="Markup medio del specialist" value={pct(c.markup_pct_avg)}
               hint="Ponderado por total_price" testid="bi-kk-kpi-markup" />
        <KpiKk label="Rentabilidad %" value={pct(c.rentabilidad_pct)}
               hint={`(Ingresado − Coste real) ÷ Total price · ${c.n_trips_con_coste} trips`}
               testid="bi-kk-kpi-rentabilidad" />
        <KpiKk label="Coste real (Σ reservas)" value={eur(c.coste_real_eur)}
               hint="Coste al proveedor operativo" testid="bi-kk-kpi-coste" />
      </div>

      {/* Gráfico mensual */}
      {chartData.length > 0 && (
        <Card data-testid="bi-kk-chart">
          <CardContent className="p-4">
            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
              <BarChart3 className="h-4 w-4 text-primary" />
              Evolución mensual {year}
            </h3>
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={72} />
                  <Tooltip formatter={(v) => eur(v)} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="Total price"       fill="#6366f1" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Ingresado"         fill="#22c55e" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Ganado por KimKim" fill="#f43f5e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabla de trips */}
      <Card data-testid="bi-kk-table">
        <CardContent className="p-0">
          <div className="border-b p-3 text-sm font-semibold">Detalle · {c.trips_count} viajes KimKim en {year}</div>
          <div className="max-h-[480px] overflow-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted/50 text-[10px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="p-2 text-left">Fecha venta</th>
                  <th className="p-2 text-left">Cliente</th>
                  <th className="p-2 text-left">Agente</th>
                  <th className="p-2 text-left">Destino</th>
                  <th className="p-2 text-left">Fuente</th>
                  <th className="p-2 text-right">Total price</th>
                  <th className="p-2 text-right">Ganado KK</th>
                  <th className="p-2 text-right">Ingresado</th>
                  <th className="p-2 text-right">Markup %</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {c.rows.slice().sort((a, b) => (b.total_price_eur || 0) - (a.total_price_eur || 0)).map((r) => (
                  <tr key={r.id} className="border-t hover:bg-muted/30">
                    <td className="p-2">{r.booking_date}</td>
                    <td className="p-2 truncate max-w-[180px]" title={r.main_traveler}>{r.main_traveler}</td>
                    <td className="p-2">{r.agent}</td>
                    <td className="p-2 truncate max-w-[140px]" title={r.destination}>{r.destination}</td>
                    <td className="p-2">
                      <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-medium ${
                        r.pricing_source === "scraped" ? "bg-emerald-100 text-emerald-800" :
                        r.pricing_source === "sofi1_migration" ? "bg-amber-100 text-amber-800" :
                        "bg-muted text-muted-foreground"}`}>
                        {sourceLbl(r.pricing_source)}
                      </span>
                    </td>
                    <td className="p-2 text-right font-semibold">{eur(r.total_price_eur)}</td>
                    <td className="p-2 text-right text-red-600">{eur(r.ganado_kk_eur)}</td>
                    <td className="p-2 text-right text-emerald-700">{eur(r.ingresado_eur)}</td>
                    <td className="p-2 text-right">{r.markup_pct != null ? `${r.markup_pct}%` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


export default function BiPage() {
  // Permisos de BI agregado: flag `ver_bi_agregado` toggleable desde /roles_perms.
  // Por defecto lo tienen Admin y Socios-Directores. NO es lo mismo que `ver_rentabilidad`
  // (panel "Rentabilidad Viajes" dentro de cada Trip, que sí ven Director de Operaciones
  // y otros liderazgos).
  const { user } = useAuth();
  const canSeeMoney = !!user?.ver_bi_agregado;

  // Keys de filas monetarias-agregadas (€ absolutos: revenue, coste, margen, mejora). Se ocultan.
  // Ratios "por pasajero" NO se ocultan porque no revelan volumen total de compañía.
  const HIDDEN_ROW_KEYS = new Set([
    "volumen_eur",
    "cotizado_eur",
    "coste_real_eur",
    "margen_venta_eur",
    "margen_venta_medio_eur",
    "margen_real_eur",
    "margen_real_medio_eur",
    "mejora_eur",
  ]);
  // Labels de KPI cards sensibles (siguen el mismo criterio que las filas).
  const HIDDEN_CARD_LABELS = new Set([
    "Volumen (precio final)",
    "Volumen de ventas",
    "Margen de venta",
    "Rentabilidad total",
    "Mejora total",
  ]);

  const filterRows = (rows) => (canSeeMoney ? rows : rows.filter((r) => !HIDDEN_ROW_KEYS.has(r.key)));
  const filterCards = (cards) => (canSeeMoney ? cards : cards.filter((c) => !HIDDEN_CARD_LABELS.has(c.label)));
  // Default KPI seguro: si el default está oculto, usa el primer row visible.
  const safeDefault = (rows, def) => {
    if (canSeeMoney) return def;
    if (rows.find((r) => r.key === def)) return def;
    return rows[0]?.key;
  };

  const ventasRows = useMemo(() => filterRows(VENTAS_ROWS), [canSeeMoney]);
  const ventasCards = useMemo(() => filterCards(VENTAS_CARDS), [canSeeMoney]);
  const rentRows = useMemo(() => filterRows(RENT_ROWS), [canSeeMoney]);
  const rentCards = useMemo(() => filterCards(RENT_CARDS), [canSeeMoney]);
  const effRows = useMemo(() => filterRows(EFF_ROWS), [canSeeMoney]);
  const effCards = useMemo(() => filterCards(EFF_CARDS), [canSeeMoney]);
  const paxRows = useMemo(() => filterRows(PAX_ROWS), [canSeeMoney]);
  const paxCards = useMemo(() => filterCards(PAX_CARDS), [canSeeMoney]);

  return (
    <div className="space-y-5" data-testid="bi-page">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight">
          <BarChart3 className="h-6 w-6 text-primary" /> Informes / BI
        </h1>
        <p className="text-sm text-muted-foreground">Rendimiento comercial y rentabilidad del equipo.</p>
      </div>
      <Tabs defaultValue="ventas">
        <TabsList data-testid="bi-tabs">
          <TabsTrigger value="ventas" data-testid="bi-tab-ventas">Ventas por agente</TabsTrigger>
          <TabsTrigger value="rentabilidad" data-testid="bi-tab-rentabilidad">Rentabilidad</TabsTrigger>
          <TabsTrigger value="eficiencia" data-testid="bi-tab-eficiencia">Eficiencia Ops.</TabsTrigger>
          <TabsTrigger value="pax" data-testid="bi-tab-pax">Por pasajero</TabsTrigger>
          {canSeeMoney && (
            <TabsTrigger value="kimkim" data-testid="bi-tab-kimkim">KimKim</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="ventas" className="mt-4">
          <MonthlyReport rows={ventasRows} cards={ventasCards} defaultKpi={safeDefault(ventasRows, "margen_venta_eur")} idPrefix="bi" fechaPor="venta"
            note="Margen de venta = la foto al vender (Ingresado − Cotizado Total). KPI de los viajes ABIERTOS. Se filtra por FECHA DE VENTA y aplica a todos los viajes vendidos." />
        </TabsContent>
        <TabsContent value="rentabilidad" className="mt-4">
          <MonthlyReport rows={rentRows} cards={rentCards} defaultKpi={safeDefault(rentRows, "rentabilidad_pct")} idPrefix="bi-rent" fechaPor="viaje"
            defaultEstados={["cerrado", "terminado"]}
            note="Rentabilidad = resultado real tras el viaje (Ingresado − Coste real). KPI de los viajes CERRADOS y TERMINADOS. Se filtra por FECHA DE VIAJE (cuándo se ejecutó)." />
        </TabsContent>
        <TabsContent value="eficiencia" className="mt-4">
          <MonthlyReport rows={effRows} cards={effCards} defaultKpi={safeDefault(effRows, "mejora_eur")} idPrefix="bi-eff" fechaPor="viaje"
            defaultEstados={["cerrado", "terminado"]}
            note="Eficiencia de Operaciones = mejora entre el Margen de venta y la Rentabilidad final (= Cotizado Total − Coste real). Positivo = Operaciones compró por debajo de lo cotizado. Se filtra por FECHA DE VIAJE. Solo viajes cerrados/terminados." />
        </TabsContent>
        <TabsContent value="pax" className="mt-4">
          <MonthlyReport rows={paxRows} cards={paxCards} defaultKpi={safeDefault(paxRows, "precio_medio_pax")} idPrefix="bi-pax" fechaPor="venta" />
        </TabsContent>
        {canSeeMoney && (
          <TabsContent value="kimkim" className="mt-4">
            <KimkimSummaryTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
